import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  X,
  Briefcase,
  FileText,
  MessageSquare,
  Settings,
  HelpCircle,
  LogOut,
  LayoutDashboard,
  Building2
} from 'lucide-react';
import useAuthStore from '../store/authStore';
import { useTenant } from '../contexts/TenantContext';

const MobileNav = ({ isOpen, onClose, navItems, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { tenant } = useTenant();

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Check if current path matches nav item
  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavClick = () => {
    onClose();
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  // Get icon for nav item
  const getNavIcon = (path) => {
    switch (path) {
      case '/dashboard':
        return <LayoutDashboard className="w-5 h-5" />;
      case '/jobs':
        return <Briefcase className="w-5 h-5" />;
      case '/proposals':
        return <FileText className="w-5 h-5" />;
      case '/messages':
        return <MessageSquare className="w-5 h-5" />;
      default:
        return <Briefcase className="w-5 h-5" />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div
        className={`fixed inset-y-0 left-0 w-72 bg-white z-50 lg:hidden transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <Link
            to="/dashboard"
            onClick={handleNavClick}
            className="flex items-center space-x-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className="w-8 h-8">
              <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
              <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
            </svg>
            <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
          </Link>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* User Info Section */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center">
              <span className="text-white font-medium text-lg">{getInitials()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || ''}
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
                </span>
                {tenant?.name && user?.role !== 'owner' && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <Building2 className="w-3 h-3" />
                    {tenant.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActiveRoute(item.path)
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className={`mr-3 ${isActiveRoute(item.path) ? 'text-primary-600' : 'text-gray-400'}`}>
                  {getNavIcon(item.path)}
                </span>
                {item.label}
              </Link>
            ))}

          </div>

          {/* Divider */}
          <div className="my-4 border-t border-gray-200"></div>

          {/* Secondary Navigation */}
          <div className="px-3 space-y-1">
            <Link
              to="/settings"
              onClick={handleNavClick}
              className="flex items-center px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <Settings className="w-5 h-5 mr-3 text-gray-400" />
              Settings
            </Link>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick();
              }}
              className="flex items-center px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              <HelpCircle className="w-5 h-5 mr-3 text-gray-400" />
              Help Center
            </a>
          </div>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Log out
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileNav;
